// src/pages/MockExamIndexPage.jsx
//
// Public hub at /:lang/mock-exams.
//
// ─── WHAT THIS PAGE IS FOR ──────────────────────────────────────────────────
// Someone searching "german mock exam" wants to know you have enough of them.
// The previous version never said how many: 201 words, a 3x3 grid of white
// cards with blue "See format" links, no numbers anywhere. It read as generated
// and it gave the visitor no reason to trust the library. This version leads
// with the scale (93 papers across 9 exams), then shows what is actually inside
// one, then lets them pick.
//
// ─── DESIGN ─────────────────────────────────────────────────────────────────
// The brand here is HomePageB's, not the blog's: warm STONE palette with the
// AMBER accent (#d97706), Space Grotesk set tight and heavy, the hand-drawn
// underline on the accent phrase, teacher avatars for authority. The blog's
// black/white treatment belongs to article pages and would read as a different
// product on a marketing page.
//
// Numbers are set oversized on purpose: font-weight 900, tight negative tracking,
// generous whitespace, high contrast, which is what the homepage hero already
// does. The palette and fonts stay the project's own.
//
// ─── RELATIONSHIP TO THE BLOG ───────────────────────────────────────────────
// Strategy content lives in the nine /blog/*-exam-guide posts, which already
// rank. This page links out to them and never restates them, so the two do not
// compete for the same query.

import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Clock, Target, FileText } from 'lucide-react';
import { useLangParam } from '../hooks/useLocalizedNavigate';
import { MOCK_EXAM_CATALOG, GERMAN_MOCK_EXAM_SLUGS, formatDuration } from '../data/mockExamCatalog';

/** Language of an exam page's URL: German where a German page exists, else English. */
const examPageLang = (slug, lang) => (lang === 'de' && !GERMAN_MOCK_EXAM_SLUGS.includes(slug) ? 'en' : lang);
import HandDrawnLine from '../components/ui/HandDrawnLine';

const BASE_URL = 'https://logami.app';
const PROVIDER_ORDER = ['TELC', 'GOETHE', 'OSD'];

const DISPLAY = { fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '-0.025em' };
const NUMERAL = { fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '-0.05em' };

/** Blog guide slug for an exam, e.g. 'telc-b2' -> 'telc-b2-exam-guide'. */
const guideSlug = (slug) => `${slug}-exam-guide`;

function Stat({ value, label, sub }) {
  return (
    <div>
      <div
        className="text-[3.25rem] sm:text-[4rem] lg:text-[4.5rem] leading-[0.9] font-bold text-stone-900 tabular-nums"
        style={NUMERAL}
      >
        {value}
      </div>
      <div className="mt-2 text-[13px] font-semibold uppercase tracking-[0.12em] text-stone-500">
        {label}
      </div>
      {sub && <div className="mt-1 text-[13px] text-stone-500 leading-snug">{sub}</div>}
    </div>
  );
}

const MockExamIndexPage = () => {
  const { t } = useTranslation();
  const lang = useLangParam();

  const totals = useMemo(() => {
    const papers = MOCK_EXAM_CATALOG.reduce((n, e) => n + e.variants, 0);
    const minutes = MOCK_EXAM_CATALOG.reduce((n, e) => n + e.totalMinutes, 0);
    return { papers, exams: MOCK_EXAM_CATALOG.length, hours: Math.round(minutes / 60) };
  }, []);

  const title = t('mockExamIndex.meta.title', {
    defaultValue: 'German Modelltest Online: telc, Goethe & ÖSD Mock Exams B1–C1 | Logami',
  });
  const description = t('mockExamIndex.meta.description', {
    papers: totals.papers,
    defaultValue:
      'Full-length Modelltests for telc, Goethe-Zertifikat and ÖSD at B1, B2 and C1. Official format, real timing, and the correct answers the moment you submit. Free to start.',
  });

  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;
    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute('content') || '';
    if (metaDesc) metaDesc.setAttribute('content', description);

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'German exam mock tests',
      numberOfItems: MOCK_EXAM_CATALOG.length,
      itemListElement: MOCK_EXAM_CATALOG.map((e, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: e.examName,
        url: `${BASE_URL}/${examPageLang(e.slug, lang)}/mock-exams/${e.slug}`,
      })),
    });
    document.head.appendChild(script);

    return () => {
      document.title = prevTitle;
      if (metaDesc) metaDesc.setAttribute('content', prevDesc);
      script.remove();
    };
  }, [title, description, lang]);

  return (
    <div className="bg-stone-50">
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-12 pb-10 sm:pt-20 sm:pb-14">
        <h1
          className="text-[2rem] leading-[1.08] sm:text-[3rem] sm:leading-[1.05] lg:text-[3.5rem] tracking-tighter text-stone-900 font-bold max-w-3xl"
          style={DISPLAY}
        >
          {t('mockExamIndex.hero.before', { defaultValue: 'Sit the real thing' })}
          <br />
          <HandDrawnLine>
            <span className="font-bold text-stone-900">
              {t('mockExamIndex.hero.accent', { defaultValue: 'before you sit the real thing.' })}
            </span>
          </HandDrawnLine>
        </h1>
        <p className="mt-6 max-w-xl text-[15px] sm:text-base text-stone-500 leading-relaxed">
          {t('mockExamIndex.hero.sub', {
            papers: totals.papers,
            defaultValue:
              'Full-length telc Übungstests, Goethe Modellsätze and ÖSD Modelltests at B1, B2 and C1. Official format, official timing, marked with the correct answers the moment you finish. No account needed to start.',
          })}
        </p>
        <Link
          to={`/${lang}/try`}
          className="mt-7 inline-flex items-center gap-2 min-h-[48px] px-6 py-3 rounded-full bg-stone-900 text-white text-[15px] font-semibold shadow-md shadow-stone-900/10 hover:bg-stone-800 active:scale-[0.97] transition-colors duration-200 cursor-pointer"
          style={DISPLAY}
        >
          {t('mockExamIndex.hero.cta', { defaultValue: 'Take a free mock exam' })}
          <ArrowRight className="w-4 h-4" aria-hidden="true" />
        </Link>
      </section>

      {/* ── Scale. The thing the old page never said. ────────────────────── */}
      <section className="border-y border-stone-200 bg-white" aria-labelledby="scale-heading">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <h2 id="scale-heading" className="sr-only">
            {t('mockExamIndex.scale.heading', { defaultValue: 'How big the library is' })}
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10">
            <Stat
              value={totals.papers}
              label={t('mockExamIndex.scale.papers', { defaultValue: 'Practice papers' })}
              sub={t('mockExamIndex.scale.papersSub', {
                defaultValue: 'Every one a complete paper, not an extract.',
              })}
            />
            <Stat
              value={totals.exams}
              label={t('mockExamIndex.scale.exams', { defaultValue: 'Exams covered' })}
              sub={t('mockExamIndex.scale.examsSub', {
                defaultValue: 'telc, Goethe-Zertifikat and ÖSD at B1, B2 and C1.',
              })}
            />
            <Stat
              value="4"
              label={t('mockExamIndex.scale.skills', { defaultValue: 'Skills in each' })}
              sub={t('mockExamIndex.scale.skillsSub', {
                defaultValue: 'Lesen, Hören, Schreiben and Sprechen, on the official clock.',
              })}
            />
            <Stat
              value={`${totals.hours}h`}
              label={t('mockExamIndex.scale.hours', { defaultValue: 'Of exam time' })}
              sub={t('mockExamIndex.scale.hoursSub', {
                defaultValue: 'One full sitting of every exam we cover.',
              })}
            />
          </div>
        </div>
      </section>

      {/* ── What is actually in a paper ──────────────────────────────────── */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <h2
          className="text-2xl sm:text-3xl tracking-tighter text-stone-900 font-bold max-w-2xl"
          style={DISPLAY}
        >
          {t('mockExamIndex.inside.heading', { defaultValue: 'What you get in every paper' })}
        </h2>
        <div className="mt-8 grid sm:grid-cols-3 gap-x-10 gap-y-8 max-w-4xl">
          {[
            {
              k: 'format',
              d: 'The official section order, question types and point split for that exam, not an approximation.',
            },
            {
              k: 'timing',
              d: 'The real clock. Each section runs for exactly as long as it does on the day.',
            },
            {
              k: 'scoring',
              d: 'A score the moment you finish, with your writing marked against the examiner rubric.',
            },
          ].map((item) => (
            <div key={item.k}>
              <h3
                className="text-[15px] font-bold text-stone-900 mb-1.5"
                style={DISPLAY}
              >
                {t(`mockExamIndex.inside.${item.k}.title`, {
                  defaultValue:
                    item.k === 'format' ? 'The exact format' : item.k === 'timing' ? 'Real timing' : 'Scored instantly',
                })}
              </h3>
              <p className="text-[14px] sm:text-[15px] text-stone-500 leading-relaxed">
                {t(`mockExamIndex.inside.${item.k}.body`, { defaultValue: item.d })}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Picker ───────────────────────────────────────────────────────── */}
      <section className="border-t border-stone-200 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <h2
            className="text-2xl sm:text-3xl tracking-tighter text-stone-900 font-bold"
            style={DISPLAY}
          >
            {t('mockExamIndex.pick.heading', { defaultValue: 'Pick your exam' })}
          </h2>

          {PROVIDER_ORDER.map((provider) => {
            const exams = MOCK_EXAM_CATALOG.filter((e) => e.provider === provider);
            if (!exams.length) return null;
            const papers = exams.reduce((n, e) => n + e.variants, 0);
            return (
              <section key={provider} className="mt-10" aria-labelledby={`provider-${provider}`}>
                <div className="flex items-baseline gap-3 border-b border-stone-200 pb-3 mb-1">
                  <h3
                    id={`provider-${provider}`}
                    className="text-lg sm:text-xl font-bold text-stone-900"
                    style={DISPLAY}
                  >
                    {exams[0].providerLabel}
                  </h3>
                  <span className="text-[13px] text-stone-500 tabular-nums">
                    {t('mockExamIndex.pick.paperCount', {
                      count: papers,
                      defaultValue: `${papers} papers`,
                    })}
                  </span>
                </div>

                <ul className="list-none pl-0 m-0">
                  {exams.map((e) => (
                    <li key={e.slug} className="border-b border-stone-200 last:border-b-0">
                      <div className="py-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
                        <div className="flex-1 min-w-0">
                          <Link
                            to={`/${examPageLang(e.slug, lang)}/mock-exams/${e.slug}`}
                            className="group inline-flex items-center gap-2 min-h-[44px] cursor-pointer"
                          >
                            <span
                              className="text-base sm:text-lg font-bold text-stone-900 group-hover:text-[#b45309] transition-colors duration-200"
                              style={DISPLAY}
                            >
                              {e.examName}
                            </span>
                            <ArrowRight
                              className="w-4 h-4 text-stone-400 group-hover:text-[#b45309] transition-colors duration-200"
                              aria-hidden="true"
                            />
                          </Link>
                          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-[13px] text-stone-500 tabular-nums">
                            <span className="inline-flex items-center gap-1.5">
                              <FileText className="w-3.5 h-3.5" aria-hidden="true" />
                              {t('mockExamIndex.card.papers', {
                                count: e.variants,
                                defaultValue: `${e.variants} papers`,
                              })}
                            </span>
                            <span className="inline-flex items-center gap-1.5">
                              <Clock className="w-3.5 h-3.5" aria-hidden="true" />
                              {formatDuration(e.totalMinutes)}
                            </span>
                            <span className="inline-flex items-center gap-1.5">
                              <Target className="w-3.5 h-3.5" aria-hidden="true" />
                              {t('mockExamIndex.card.points', {
                                count: e.totalPoints,
                                defaultValue: `${e.totalPoints} points`,
                              })}
                            </span>
                          </div>
                        </div>

                        {/* Strategy lives in the blog guide. Link, never restate. */}
                        <Link
                          to={`/${lang}/blog/${guideSlug(e.slug)}`}
                          className="shrink-0 inline-flex items-center min-h-[44px] text-[13px] font-semibold text-stone-500 hover:text-stone-900 underline underline-offset-4 decoration-stone-300 hover:decoration-stone-900 transition-colors duration-200 cursor-pointer"
                        >
                          {t('mockExamIndex.card.guide', {
                            seoName: e.seoName,
                            defaultValue: `Read the ${e.seoName} guide`,
                          })}
                        </Link>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default MockExamIndexPage;
